import { useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Button, Center, Flex, SimpleGrid, Spinner, Text } from '@chakra-ui/react';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';

import { format, parseISO } from 'date-fns';

import {
  DropdownOption,
  FilterData,
  Filters,
  IndividualDeductionsReportStatus,
  MultiSelectionData,
  ArrowLeftOutline,
  Badge,
  BadgeStatus,
  DocumentDownloadOutline,
  RefreshOutline,
  useToast,
} from '@aslanpay/portal-common-lib';

import DeductionsReportErrors from 'app/components/DeductionReports/DeductionsReportErrors';
import PayCycleSummary from 'app/components/PayCalendars/PayCycleSummary';
import RetryDeductionsReportDialog from 'app/components/DeductionReports/RetryDeductionsReportDialog';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import ProfileField from 'app/components/UI/ProfileField';
import { Table } from 'app/components/UI/Table/Table';
import {
  IndividualDeductionsReportActions,
  IndividualDeductionsReportRow,
} from 'app/components/UI/Table/Table.types';

import IndividualDeductionsReportsStatusValues from 'app/constants/individualDeductionsReports';
import DeductionsReportsStatusValues from 'app/constants/deductionsReports';
import { defaultPageSize } from 'app/constants/table';

import useFetchDeductionsReportsPage from 'app/hooks/useFetchDeductionsReportsPage';
import useFetchIndividualDeductionsReportsPage from 'app/hooks/useFetchIndividualDeductionsReportsPage';
import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';
import useDownloadDeductionsReport from 'app/hooks/useDownloadDeductionsReport';
import useRetryDeductionsReport from 'app/hooks/useRetryDeductionsReport';

import { useNavigationStore } from 'app/store';

import {
  isDownloadableDeductionsReportStatus,
  isRetryableDeductionsReport,
} from 'app/utils/deductionsReports';
import { formatCurrency } from 'app/utils/strings';
import {
  getIndividualDeductionsReportProfilePath,
  getPayCalendarProfilePath,
  getSelectedCompanyPath,
} from 'app/utils/routes';
import getIndividualDeductionsReportsColumns from 'app/utils/table/individualDeductionsReports';

import type { Filter } from '@aslanpay/portal-common-lib';

const PAGE_SIZE_OPTIONS = [
  { value: 15, text: '15 rows / page' },
  { value: 25, text: '25 rows / page' },
  { value: 50, text: '50 rows / page' },
];
const LOCAL_SORT_PAGE_SIZE = 999999;

const formatCutoffDate = (cutoffDate?: string) =>
  cutoffDate ? format(parseISO(cutoffDate), 'dd MMM yyyy') : undefined;

/** Displays one company deductions report and all of its individual reports. */
function CompanyDeductionsReportProfile() {
  const navigate = useNavigate();
  const toast = useToast();
  const { deductionsReportId } = useParams();
  const view = useNavigationStore((state) => state.view);
  const isGlobalView = view === 'global';

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [statusFilter, setStatusFilter] = useState<IndividualDeductionsReportStatus[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'employeeName', desc: false }]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false);
  const isLocallySorted = sorting[0]?.id === 'employeeName';
  const serverSortDirection = sorting[0]?.desc ? 'DESC' : 'ASC';

  const { data: companyReports, isLoading: isLoadingCompanyReport } = useFetchDeductionsReportsPage(
    {
      ids: deductionsReportId ? [deductionsReportId] : [],
      page: 0,
      size: 1,
    }
  );
  const companyReport = companyReports?.list[0];
  const { data: payCalendar, isLoading: isLoadingPayCalendar } = useFetchPayCalendar(
    companyReport?.payCalendarId
  );
  const payCycles = [...(payCalendar?.payCycles || [])].sort(
    (left, right) => parseISO(left.startDate).getTime() - parseISO(right.startDate).getTime()
  );
  const payCycleIndex = payCycles.findIndex((cycle) => cycle.id === companyReport?.payCycleId);
  const payCycle = payCycleIndex >= 0 ? payCycles[payCycleIndex] : undefined;
  const previousPayCycle = payCycleIndex > 0 ? payCycles[payCycleIndex - 1] : undefined;
  const currentCutoffDate = formatCutoffDate(payCycle?.cutoffDate);
  const cutoffRange = [formatCutoffDate(previousPayCycle?.cutoffDate), currentCutoffDate]
    .filter(Boolean)
    .join(' - ');
  const reportTitle = [payCalendar?.name || companyReport?.payCalendarName, cutoffRange]
    .filter(Boolean)
    .join(' - ');

  const { data: individualReports, isLoading: isLoadingIndividualReports } =
    useFetchIndividualDeductionsReportsPage(
      {
        companyIds: companyReport ? [companyReport.companyId] : [],
        payCycleIds: companyReport ? [companyReport.payCycleId] : [],
        status: statusFilter,
        sortBy: isLocallySorted ? 'externalPayrollId' : sorting[0]?.id || 'externalPayrollId',
        sort: isLocallySorted ? 'ASC' : serverSortDirection,
        page: isLocallySorted ? 0 : pageIndex,
        size: isLocallySorted ? LOCAL_SORT_PAGE_SIZE : pageSize,
      },
      Boolean(companyReport)
    );

  const statusOptions: DropdownOption[] = Object.entries(
    IndividualDeductionsReportsStatusValues
  ).map(([value, { value: text }]) => ({ value, text }));
  const filters: Filter[] = [
    {
      name: 'Report status',
      type: 'category',
      data: statusOptions,
      selectedValues: statusFilter,
    },
  ];

  const sortedIndividualReports = useMemo(() => {
    if (!isLocallySorted) {
      return individualReports?.list || [];
    }

    const [sort] = sorting;
    if (!sort) {
      return individualReports?.list || [];
    }

    return [...(individualReports?.list || [])].sort((left, right) => {
      const leftValue = left[sort.id as keyof IndividualDeductionsReportRow];
      const rightValue = right[sort.id as keyof IndividualDeductionsReportRow];
      const comparison = String(leftValue ?? '').localeCompare(
        String(rightValue ?? ''),
        undefined,
        { numeric: true, sensitivity: 'base' }
      );

      return sort.desc ? -comparison : comparison;
    });
  }, [individualReports?.list, isLocallySorted, sorting]);

  const visibleIndividualReports = isLocallySorted
    ? sortedIndividualReports.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)
    : sortedIndividualReports;

  const actions: IndividualDeductionsReportActions = {
    select: (id: string) => navigate(getIndividualDeductionsReportProfilePath(id)),
  };

  const { mutate: downloadDeductionsReport } = useDownloadDeductionsReport({
    onSuccess: () => {
      toast({ position: 'top-right', status: 'success', title: 'Report downloaded' });
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to download the file. Please try again.',
      });
    },
  });

  const { mutate: retryDeductionsReport, isPending: isRetryingDeductionsReport } =
    useRetryDeductionsReport();

  const handleConfirmRetry = () => {
    if (!companyReport) {
      return;
    }

    retryDeductionsReport(
      { id: companyReport.id },
      { onSettled: () => setIsRetryDialogOpen(false) }
    );
  };

  const handleStatusChange = (values: (string | number)[]) => {
    setStatusFilter(values as IndividualDeductionsReportStatus[]);
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) => {
    return (
      (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
      String(filterValue)
    );
  };

  if (isLoadingCompanyReport || isLoadingPayCalendar || isLoadingIndividualReports) {
    return (
      <AuthenticatedPageWrapper title="Company deduction report">
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" />
        </Center>
      </AuthenticatedPageWrapper>
    );
  }

  return (
    <AuthenticatedPageWrapper title="Company deduction report">
      <Button
        my="10px"
        alignSelf="flex-start"
        size="sm"
        variant="link"
        leftIcon={<ArrowLeftOutline boxSize="20px" />}
        onClick={() => navigate(-1)}
      >
        Back to deduction reports
      </Button>

      <Flex align="center" justify="space-between" gap="20px">
        <Text as="h2" textStyle="h2" color="brand.darkPurple">
          {reportTitle || 'Deduction report'}
        </Text>
        <Flex gap="10px">
          {isRetryableDeductionsReport(companyReport) && (
            <Button
              leftIcon={<RefreshOutline boxSize="20px" />}
              size="sm"
              variant="outline"
              isDisabled={isRetryingDeductionsReport}
              onClick={() => setIsRetryDialogOpen(true)}
              data-testid="retry-deductions-report-button"
            >
              Retry
            </Button>
          )}
          <Button
            leftIcon={<DocumentDownloadOutline boxSize="20px" />}
            size="sm"
            isDisabled={!isDownloadableDeductionsReportStatus(companyReport?.status)}
            onClick={() => companyReport && downloadDeductionsReport({ id: companyReport.id })}
          >
            Download report
          </Button>
        </Flex>
      </Flex>

      <RetryDeductionsReportDialog
        isOpen={isRetryDialogOpen}
        isRetrying={isRetryingDeductionsReport}
        onClose={() => setIsRetryDialogOpen(false)}
        onConfirm={handleConfirmRetry}
      />

      <SimpleGrid
        mt="20px"
        w="full"
        columns={{ base: 1, md: 3, xl: isGlobalView ? 6 : 5 }}
        spacing="20px"
        p="20px"
        border="1px solid"
        borderColor="brand.lightGray1"
        borderRadius="10px"
      >
        {isGlobalView && (
          <ProfileField
            label="Company"
            value={companyReport?.companyName}
            link={companyReport ? getSelectedCompanyPath(companyReport.companyId) : undefined}
          />
        )}
        <ProfileField
          label="Pay calendar"
          value={payCalendar?.name || companyReport?.payCalendarName}
          link={
            companyReport
              ? getPayCalendarProfilePath(companyReport.payCalendarId, companyReport.companyId)
              : undefined
          }
        />
        <Flex gap="5px" flexDir="column" w="full">
          <Text color="brand.black" textStyle="extraSmallRegular">
            Status
          </Text>
          <Flex minH="52px" align="center">
            {companyReport && (
              <Badge
                value={DeductionsReportsStatusValues[companyReport.status]?.value ?? '-'}
                status={
                  (DeductionsReportsStatusValues[companyReport.status]?.color ??
                    'created') as BadgeStatus
                }
              />
            )}
          </Flex>
        </Flex>
        <Flex gap="5px" flexDir="column" w="full" data-testid="report-pay-cycle">
          <Text color="brand.black" textStyle="extraSmallRegular">
            Pay cycle
          </Text>
          <Flex minH="52px" align="center">
            <PayCycleSummary payCycle={payCycle} />
          </Flex>
        </Flex>
        <ProfileField label="Records" value={companyReport?.totalRecords?.toString()} />
        <Flex gap="5px" flexDir="column" w="full" alignItems={{ xl: 'flex-end' }}>
          <Text color="brand.black" textStyle="extraSmallRegular">
            Deduction amount
          </Text>
          <Text py="15px" color="brand.black" textStyle="smallSemiBold">
            {formatCurrency(companyReport?.salaryDeductions || 0, companyReport?.currency)}
          </Text>
        </Flex>
      </SimpleGrid>

      <DeductionsReportErrors
        deductionsReportId={companyReport?.id}
        status={companyReport?.status}
      />

      <Flex mt="20px" flexDir="column" gap="20px">
        <Text as="h3" textStyle="h3" color="brand.darkPurple">
          Individual reports
        </Text>
        <Filters
          activeFilter={activeFilter}
          displayFormatter={formatFilterValue}
          filters={filters}
          onFilterClose={() => setActiveFilter(undefined)}
          onFilterChange={(_, values) => handleStatusChange(values)}
          onFilterClick={setActiveFilter}
        />
        <Table
          columns={getIndividualDeductionsReportsColumns(actions, true, false, true)}
          data={visibleIndividualReports}
          total={individualReports?.total || 0}
          pageNumber={pageIndex}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          getRowId={(report) => report.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
        />
      </Flex>
    </AuthenticatedPageWrapper>
  );
}

export default CompanyDeductionsReportProfile;
